import {FooterTabFragmentProps, FooterTabFragmentState} from "../../shared/FooterTabFragment";
import React from "react";
import {StyledBoxColumn, StyledBoxRow, StyledContainer} from "../../shared/StyledComponents";
import {Card, Grid, LinearProgress, Typography} from "@mui/material";
import {colorPastelYellow, pastelColors} from "../../shared/colors";
import {Avatar} from "../avatarmaker/avatar";
import {PAGE_FRAGMENT_HALF_WIDTH, PD_LG, PD_MD, PD_SM, PD_XLG2, SZ_MD} from "../../shared/dimens";
import {getMemberAuth} from "../../shared/auth";
import {$KTM, KeyMetadatas} from "../../shared/types";
import streakIcon from "./res/icons/streak.svg";
import pointsIcon from "./res/icons/points.svg";
import badgesIcon from "./res/icons/badges.svg";
import masteryIcon from "./res/icons/mastery.svg";
import practice5Icon from "./res/icons/achievements/practice_5.svg";
import {ContainerWithTitle} from "../util/Components";
import {BaseAppTabFragment} from "../BaseAppTabFragment";

enum StatisticType {
  STREAK = "streak",
  POINTS = "points",
  BADGES = "badges",
  MASTERY = "mastery",
}

type StatisticMetadata = {
  description: string,
  iconUrl: string,
}

const STATISTIC_KTM = new KeyMetadatas<StatisticMetadata>([
  $KTM(StatisticType.STREAK, {description: "Day streak", iconUrl: streakIcon}),
  $KTM(StatisticType.POINTS, {description: "Points", iconUrl: pointsIcon}),
  $KTM(StatisticType.BADGES, {description: "Badges", iconUrl: badgesIcon}),
  $KTM(StatisticType.MASTERY, {description: "Mastery", iconUrl: masteryIcon}),
]);

enum AchievementType {
  PRACTICE_5 = "practice_5",
}

type AchievementMetadata = {
  description: string,
  iconUrl: string,
  progressMin: number,
  progressMax: number,
  level: number,
}

const ACHIEVEMENT_LEVEL_COLORS: string[] = [
  colorPastelYellow,
];

const ACHIEVEMENT_KTM = new KeyMetadatas<AchievementMetadata>([
  $KTM(AchievementType.PRACTICE_5, {
    description: "Completed chats with 5 characters.",
    iconUrl: practice5Icon,
    progressMin: 0,
    progressMax: 5,
    level: 0,
  }),
]);

type HomeFragmentProps = FooterTabFragmentProps & {}

type HomeFragmentState = FooterTabFragmentState & {
  previewSvg: string,
}

export class HomeFragment extends BaseAppTabFragment<HomeFragmentProps, HomeFragmentState> {

  private readonly memberAuth = getMemberAuth();

  getTitle(): string {
    return "Home";
  }

  protected async fetchOnMount(forceReload?: boolean): Promise<any> {
    this.setState({
      previewSvg: await Avatar.generateRandom(pastelColors[Math.floor(Math.random() * pastelColors.length)], true),
    });
  }

  private getAchievementValue(achievement: AchievementType): number {
    return 0;
  }

  renderTabContent(): React.ReactElement {
    return <StyledContainer style={{padding: PD_LG, gap: PD_XLG2}}>
      <Card>
        <StyledBoxRow style={{padding: "4%", alignItems: 'center', gap: 0}}>
          <StyledBoxColumn style={{flexBasis: 0, flexGrow: 1, maxWidth: PAGE_FRAGMENT_HALF_WIDTH}}>
            <Typography variant="h4">Welcome {this.memberAuth.member.user.firstname}.</Typography>
            <Typography>It's great to have you here. Let's get started on your journey to better communication and
              connection.</Typography>
          </StyledBoxColumn>
          <div
            id="avatar-preview"
            style={{
              flexBasis: 0,
              flexGrow: 1,
              alignSelf: "center",
              aspectRatio: 1,
            }}
            dangerouslySetInnerHTML={{
              __html: this.state.previewSvg,
            }}
          />
        </StyledBoxRow>
      </Card>
      <ContainerWithTitle title={"Statistics"}>
        <Grid container spacing={2}>
          {Object.getOwnPropertyNames(StatisticType).map(name => {
            const stat = StatisticType[name];
            const meta = STATISTIC_KTM.findMetadata(stat);
            return <Grid item xs={6}>
              <Card>
                <StyledBoxRow style={{alignItems: "center", padding: PD_SM}}>
                  <img src={meta.iconUrl} style={{width: SZ_MD, aspectRatio: 1, padding: PD_MD}}/>
                  <StyledBoxColumn style={{flexGrow: 1, gap: 2}}>
                    <Typography variant="h5">{0}</Typography>
                    <Typography>{meta.description}</Typography>
                  </StyledBoxColumn>
                </StyledBoxRow>
              </Card>
            </Grid>;
          })}
        </Grid>
      </ContainerWithTitle>
      <ContainerWithTitle title="Achievements">
        <Grid container spacing={2}>
          {Object.getOwnPropertyNames(AchievementType).map(name => {
            const type = AchievementType[name];
            const meta = ACHIEVEMENT_KTM.findMetadata(type);
            const value = this.getAchievementValue(type);
            return <Grid item xs={12}>
              <Card>
                <StyledBoxRow style={{alignItems: "center", padding: PD_LG, gap: PD_LG}}>
                  <div style={{background: ACHIEVEMENT_LEVEL_COLORS[meta.level], aspectRatio: 1}}>
                    <img src={meta.iconUrl}
                         style={{verticalAlign: "top", width: SZ_MD, aspectRatio: 1, padding: PD_MD}}/>
                  </div>
                  <StyledBoxColumn style={{flexGrow: 1}}>
                    <Typography variant="h5">{value}</Typography>
                    <LinearProgress value={((value - meta.progressMin) * 100) / (meta.progressMax - meta.progressMin)}
                                    variant={"determinate"}/>
                    <Typography>{meta.description}</Typography>
                  </StyledBoxColumn>
                </StyledBoxRow>
              </Card>
            </Grid>;
          })}
        </Grid>
      </ContainerWithTitle>
    </StyledContainer>;
  }
}